import type { Post } from '../posts.js';

/**
 * US-English long-form date ("May 17, 2026") matching the landing
 * footer's rendering. `timeZone: 'UTC'` pins the result so SSR
 * and client hydration agree regardless of the visitor's local
 * timezone — otherwise a `2026-05-17` frontmatter date can
 * display as "May 16, 2026" for visitors west of UTC.
 */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Title block for a single post detail page (`/blogs/<slug>/`).
 *
 * Sits above `<Prose>`; uses the same `max-w-prose` width so the
 * H1, description, and metadata line up with the body text below.
 * Breadcrumb is intentionally tiny — orientation only, the post
 * title is the dominant visual anchor.
 */
export function ArticleHeader({ post }: { readonly post: Post }): JSX.Element {
  return (
    <header className="mx-auto max-w-prose">
      {/* Terminal-path eyebrow matches the rest of the site
          (`~/memento`, `~/quickstart`, `~/packs`, …). On the
          /blogs/ index this same string renders as static text;
          on a post detail page it doubles as the navigation
          link back to the index. */}
      <p className="font-mono text-xs uppercase tracking-widish text-muted">
        <a
          href="/blogs/"
          className="underline decoration-border underline-offset-4 hover:text-fg hover:decoration-accent"
        >
          ~/memento/blogs
        </a>
      </p>
      <h1 className="mt-6 text-4xl font-medium leading-tight tracking-tight text-fg md:text-5xl">
        {post.title}
      </h1>
      <p className="mt-6 text-lg leading-relaxed text-fg/75 md:text-xl">{post.description}</p>
      {/* Meta row: byline, published date, reading time, tags,
          optional updated date. Byline is hard-coded as a single
          author (this is a single-maker project today); if/when
          multi-author becomes a real thing, the byline moves into
          the `Post` type via frontmatter. `rel="author"` is the
          conventional hint that crawlers and parsers use to
          associate the link target with the post's author. */}
      <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-xs uppercase tracking-widish text-muted">
        <span>
          by{' '}
          <a
            href="https://github.com/veerps57"
            rel="author"
            className="underline decoration-border underline-offset-4 hover:text-fg hover:decoration-accent"
          >
            Raghu
          </a>
        </span>
        <span aria-hidden>·</span>
        <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
        <span aria-hidden>·</span>
        <span>{post.readingTime} min read</span>
        {post.tags.length > 0 ? <span aria-hidden>·</span> : null}
        {post.tags.map((tag, idx) => (
          <span key={tag}>
            <span>{tag}</span>
            {idx < post.tags.length - 1 ? <span aria-hidden> · </span> : null}
          </span>
        ))}
        {post.updatedAt !== undefined ? (
          <>
            <span aria-hidden>·</span>
            <span>updated {formatDate(post.updatedAt)}</span>
          </>
        ) : null}
      </div>
      <hr className="mt-10 border-border" />
    </header>
  );
}
